import { nip04, finalizeEvent } from 'nostr-tools';

/**
 * Unified NIP-04 (Direct Messages) Encryption Service
 * Handles both Extension and Seed users
 */
export const nip04Service = {
    /**
     * Encrypt message for a recipient
     */
    /**
     * Helper to determine encryption method
     */
    getBestMethod(method: 'extension' | 'seed' | 'read_only', sk?: Uint8Array): 'extension' | 'seed' {
        if (method === 'extension') return 'extension';
        if (sk) return 'seed';
        if (window.nostr?.nip04) return 'extension';
        return 'seed'; // Will throw later if sk is missing
    },

    /**
     * Encrypt message for a recipient
     */
    async encrypt(
        content: string,
        recipientPubkey: string,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        try {
            if (method === 'extension') {
                if (!window.nostr?.nip04) {
                    throw new Error('Nostr extension (NIP-04) not found or not authorized.');
                }
                return await window.nostr.nip04.encrypt(recipientPubkey, content);
            } else {
                if (!sk) throw new Error('Secret key missing. Please log in.');
                return await nip04.encrypt(sk, recipientPubkey, content);
            }
        } catch (error) {
            console.error('❌ [NIP04Service] Encryption failed:', error);
            throw error;
        }
    },

    /**
     * Decrypt message from a sender
     */
    async decrypt(
        ciphertext: string,
        otherPubkey: string,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        try {
            if (method === 'extension') {
                if (!window.nostr?.nip04) {
                    throw new Error('Nostr extension (NIP-04) not found');
                }
                return await window.nostr.nip04.decrypt(otherPubkey, ciphertext);
            } else {
                if (!sk) throw new Error('Secret key required for Seed decryption');
                return await nip04.decrypt(sk, otherPubkey, ciphertext);
            }
        } catch (error) {
            // Log decryption failure but don't throw (some messages on relays might not be for us)
            console.warn(`⚠️ [NIP04Service] Decryption failed for ${otherPubkey.slice(0, 8)}...`);
            throw error;
        }
    },

    /**
     * sign any event
     */
    async signEvent(
        event: any,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<any> {
        try {
            if (method === 'extension') {
                if (!window.nostr?.signEvent) {
                    throw new Error('Nostr extension (signEvent) not found');
                }
                return await window.nostr.signEvent(event);
            } else {
                if (!sk) throw new Error('Secret key required for signing');
                return finalizeEvent(event, sk);
            }
        } catch (error) {
            console.error('❌ [NIP04Service] Signing failed:', error);
            throw error;
        }
    }
};
